<template>
  <div
    ref="videoBackgroundRef"
    :class="{
      [lightTextClass]: true,
      'video-background-wrapper relative overflow-hidden': true,
    }"
  >
    <!-- Fallback image shown until video is playing -->
    <div
      v-if="fallbackImageUrl && !isVideoPlaying"
      aria-hidden="true"
      class="pointer-events-none absolute inset-0 h-full w-full bg-cover bg-center bg-no-repeat"
      :style="{ backgroundImage: `url(${fallbackImageUrl})` }"
    ></div>
    <div
      v-if="isVisible && hashed_id"
      aria-hidden="true"
      :class="{
        [wistiaEmbedClasses]: true,
        'pointer-events-none absolute inset-0 h-full w-full': true,
      }"
    ></div>
    <div :class="{ relative: true }">
      <slot />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useIntersectionObserver } from '@vueuse/core';
import type { VideoBackground } from '~/sanity/types';
import { useWistiaVideo } from '~/composables/useWistiaVideo';

const props = defineProps<VideoBackground>();
const { lightText, fallbackImage } = toRefs(props);

const isVisible = ref(false);
const videoBackgroundRef = useTemplateRef<HTMLElement>('videoBackgroundRef');

const { isPlaying: isVideoPlaying } = useWistiaVideo({ hashedId: () => props.hashed_id });

// Generate fallback image URL from Sanity asset
const fallbackImageUrl = computed(() => {
  if (fallbackImage.value?.asset?._ref) {
    return cdnImage(fallbackImage.value.asset._ref).bg;
  }
  return null;
});

const { stop } = useIntersectionObserver(
  videoBackgroundRef,
  ([entry]) => {
    // once it's visible, keep it visible
    isVisible.value = isVisible.value || (entry?.isIntersecting ?? false);
    if (isVisible.value) {
      stop();
    }
  },
  {
    threshold: 0.01,
    rootMargin: '250px',
  }
);

// Wistia reads its embed options from the class list of the target element
const wistiaEmbedOptions = [
  'autoPlay=true',
  'silentAutoPlay=allow',
  'muted=true',
  'endVideoBehavior=loop',
  'fitStrategy=cover',
  'videoFoam=false',
  'controlsVisibleOnLoad=false',
  'playbar=false',
  'playButton=false',
  'smallPlayButton=false',
  'settingsControl=false',
  'volumeControl=false',
  'fullscreenButton=false',
  'plugin[captions-v1][on]=false',
].join(' ');

const wistiaEmbedClasses = computed(
  () => `wistia_embed wistia_async_${props.hashed_id} ${wistiaEmbedOptions}`
);

const lightTextClass = computed(() => {
  return lightText.value ? 'text-www-color-white' : 'text-www-color-neutral-medium-dark';
});
</script>

<style scoped></style>
